import Swiper from "swiper";
import { Autoplay, EffectFade } from "swiper/modules";

document.addEventListener("DOMContentLoaded", () => {
  const root = document.documentElement;
  const body = document.body;

  // ─── Light / dark switch (initial value is set by the inline head script) ──
  document.querySelectorAll(".js-theme-toggle").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = root.getAttribute("data-theme") === "dark" ? "light" : "dark";
      root.setAttribute("data-theme", next);
      try { localStorage.setItem("bz-theme", next); } catch (e) { /* storage blocked */ }
    });
  });

  // ─── Mobile menu ───────────────────────────────────────────────────────────
  const burger = document.querySelector(".js-burger");
  const menu = document.querySelector(".js-header-nav");
  const closeMenu = () => {
    if (!burger || !menu) return;
    menu.classList.remove("active");
    burger.classList.remove("active");
    burger.setAttribute("aria-expanded", "false");
    body.classList.remove("locked");
  };
  if (burger && menu) {
    burger.addEventListener("click", () => {
      const open = !menu.classList.contains("active");
      menu.classList.toggle("active", open);
      burger.classList.toggle("active", open);
      burger.setAttribute("aria-expanded", open ? "true" : "false");
      body.classList.toggle("locked", open);
    });
    menu.addEventListener("click", (e) => { if (e.target.closest("a")) closeMenu(); });
    window.addEventListener("resize", () => { if (window.innerWidth >= 992) closeMenu(); });
  }

  // ─── Search overlay ────────────────────────────────────────────────────────
  const overlay = document.querySelector(".js-search-overlay");
  const openBtn = document.querySelector(".js-open-search");
  const closeBtn = document.querySelector(".js-close-search");
  if (overlay && openBtn && closeBtn) {
    const close = () => { overlay.classList.remove("active"); body.classList.remove("locked"); openBtn.focus(); };
    openBtn.addEventListener("click", (e) => {
      e.preventDefault();
      closeMenu();
      overlay.classList.add("active");
      body.classList.add("locked");
      setTimeout(() => { const i = overlay.querySelector("input"); if (i) i.focus(); }, 120);
    });
    closeBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && overlay.classList.contains("active")) close();
    });
  }

  // ─── Hero slider (Swiper) ──────────────────────────────────────────────────
  const heroEl = document.querySelector(".js-hero-slider");
  if (heroEl) {
    const slides = heroEl.querySelectorAll(".swiper-slide").length;
    const cur = heroEl.querySelector(".js-hero-current");
    const bar = heroEl.querySelector(".js-hero-bar");
    const delay = 6500;
    heroEl.style.setProperty("--slide-ms", delay + "ms");
    const restartBar = () => {
      if (!bar) return;
      bar.style.animation = "none";
      void bar.offsetWidth; // reflow
      bar.style.animation = "";
    };

    if (slides > 1) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const hero = new Swiper(heroEl, {
        modules: [Autoplay, EffectFade],
        effect: "fade",
        fadeEffect: { crossFade: true },
        loop: true,
        speed: 700,
        autoplay: reduced ? false : { delay, disableOnInteraction: false, pauseOnMouseEnter: true },
        on: {
          slideChange(s) {
            if (cur) cur.textContent = String(s.realIndex + 1).padStart(2, "0");
            restartBar();
          },
        },
      });
      const prev = heroEl.querySelector(".js-hero-prev");
      const next = heroEl.querySelector(".js-hero-next");
      if (prev) prev.addEventListener("click", () => hero.slidePrev());
      if (next) next.addEventListener("click", () => hero.slideNext());
      heroEl.addEventListener("mouseenter", () => { if (bar) bar.style.animationPlayState = "paused"; });
      heroEl.addEventListener("mouseleave", () => { if (bar) bar.style.animationPlayState = "running"; });
    }
  }

  // ─── Scroll-snap rails (trending) ──────────────────────────────────────────
  document.querySelectorAll(".js-rail").forEach((rail) => {
    const track = rail.querySelector(".js-rail-track");
    const prev = rail.querySelector(".js-rail-prev");
    const next = rail.querySelector(".js-rail-next");
    if (!track) return;
    const step = () => Math.max(track.clientWidth * 0.8, 240);
    const sync = () => {
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    if (prev) prev.addEventListener("click", () => track.scrollBy({ left: -step(), behavior: "smooth" }));
    if (next) next.addEventListener("click", () => track.scrollBy({ left: step(), behavior: "smooth" }));
    track.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    sync();

    // Drag-to-scroll with a mouse
    let down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener("pointerdown", (e) => {
      if (e.pointerType !== "mouse") return;
      down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
    });
    window.addEventListener("pointermove", (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) { moved = true; track.style.scrollSnapType = "none"; track.style.scrollBehavior = "auto"; }
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener("pointerup", () => {
      if (!down) return;
      down = false;
      track.style.scrollSnapType = ""; track.style.scrollBehavior = "";
    });
    track.addEventListener("click", (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
  });

  // ─── Scroll reveal ─────────────────────────────────────────────────────────
  const revealEls = document.querySelectorAll(".js-reveal");
  if (revealEls.length) {
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); } });
      }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
      revealEls.forEach((el) => io.observe(el));
    } else {
      revealEls.forEach((el) => el.classList.add("is-in"));
    }
  }

  // ─── Reading progress (single posts) ───────────────────────────────────────
  const progress = document.querySelector(".js-progress");
  const prose = document.querySelector(".js-prose");
  if (progress && prose) {
    const update = () => {
      const rect = prose.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const done = Math.min(Math.max(-rect.top + window.innerHeight * 0.3, 0), Math.max(total, 1));
      progress.style.setProperty("--p", (done / Math.max(total, 1)).toFixed(4));
    };
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
  }

  // ─── Copy link ─────────────────────────────────────────────────────────────
  document.querySelectorAll(".js-copy-link").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const url = btn.dataset.url || window.location.href;
      try {
        await navigator.clipboard.writeText(url);
      } catch (e) {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta); ta.select();
        try { document.execCommand("copy"); } catch (err) { /* ignore */ }
        ta.remove();
      }
      btn.classList.add("is-copied");
      btn.setAttribute("title", btn.dataset.done || "Copied!");
      setTimeout(() => btn.classList.remove("is-copied"), 1800);
    });
  });

  // ─── Back to top ───────────────────────────────────────────────────────────
  const backToTop = document.querySelector(".js-back-to-top");
  if (backToTop) {
    const toggle = () => backToTop.classList.toggle("is-visible", window.scrollY > 500);
    window.addEventListener("scroll", toggle, { passive: true });
    toggle();
    backToTop.addEventListener("click", () => window.scrollTo({ top: 0, behavior: "smooth" }));
  }
});
